/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useMemo } from 'react';

import { cssMap, jsx } from '@compiled/react';
import type { IntlShape } from 'react-intl';
import { IntlProvider } from 'react-intl';

import { aiSuggestionsMessages } from '@atlaskit/editor-common/ai-messages';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import type { AiSuggestionsPlugin, Suggestion } from '../../aiSuggestionsPluginType';

import { SuggestionIcon } from './SuggestionIcon';

const suggestionIconScaleVar = '--suggestion-icon-scale';
const suggestionIconRotateVar = '--suggestion-icon-rotate';

const styles = cssMap({
	container: {
		position: 'absolute',
		transform: 'translateX(100%)',
		pointerEvents: 'all',
	},
	button: {
		boxSizing: 'border-box',
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
		alignItems: 'center',
		height: '24px',
		width: '24px',
		border: 'none',
		backgroundColor: token('color.background.neutral.subtle'),
		borderRadius: token('radius.full'),
		outline: 'none',
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
		[suggestionIconScaleVar]: '1',
		[suggestionIconRotateVar]: '0deg',
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
			[suggestionIconScaleVar]: 'calc(4/3)',
			[suggestionIconRotateVar]: '90deg',
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
		'&:focus-visible': {
			outlineStyle: 'solid',
			outlineWidth: token('border.width.focused'),
			outlineColor: token('color.border.focused'),
		},
	},
	svgContainer: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		transition: 'transform 0.2s ease-in-out',
		transformOrigin: 'center center',
		transform: `scale(var(${suggestionIconScaleVar})) rotate(var(${suggestionIconRotateVar}))`,
	},
});

export function SuggestionIconButton({
	leftAdjustment,
	suggestion,
	suggestionsCount,
	isSelected,
	api,
	intl,
	topAdjustment,
}: {
	api?: ExtractInjectionAPI<AiSuggestionsPlugin>;
	intl?: IntlShape;
	isSelected: boolean;
	leftAdjustment?: number;
	suggestion: Suggestion;
	suggestionsCount: number;
	// Used to visually adjust the position so it lines up with block controls
	topAdjustment?: number;
}): JSX.Element {
	const { baseLocalIdBySuggestionId } = useSharedPluginStateWithSelector(
		api,
		['aiSuggestions'],
		(state) => {
			const { baseLocalIdBySuggestionId } = state?.aiSuggestionsState ?? {};

			return {
				baseLocalIdBySuggestionId,
			};
		},
	);

	const onSelectSuggestion = () => {
		api?.aiSuggestions?.actions.selectSuggestion(suggestion.id);
	};

	const tooltipContent = useMemo(
		() =>
			suggestionsCount > 1
				? intl?.formatMessage(aiSuggestionsMessages.iconButtonSuggestionsCountLabel, {
						count: suggestionsCount,
					}) || suggestion.shortDescription
				: suggestion.shortDescription,
		[intl, suggestion.shortDescription, suggestionsCount],
	);

	return (
		<IntlProvider
			defaultLocale={intl?.defaultLocale || 'en-US'}
			locale={intl?.locale || 'en-US'}
			messages={intl?.messages}
			formats={intl?.formats}
		>
			<span
				css={styles.container}
				style={{
					anchorName: `--button-${baseLocalIdBySuggestionId?.get(suggestion.id)}`,
					top: topAdjustment ?? 0,
					right: `calc(${token('space.negative.300')} + ${token('space.negative.300')} - ${
						leftAdjustment ?? 0
					}px)`,
				}}
			>
				{
					// We keep the anchorName span rendered so that the suggestion card can reference it
					!isSelected && (
						<Tooltip content={tooltipContent} position="right">
							<Pressable
								type="button"
								aria-label={tooltipContent}
								xcss={styles.button}
								onClick={onSelectSuggestion}
							>
								<div css={styles.svgContainer}>
									<SuggestionIcon label="" size="medium" />
								</div>
							</Pressable>
						</Tooltip>
					)
				}
			</span>
		</IntlProvider>
	);
}
